/**
 * Recruitment ATS — formatting helpers.
 * India formatting per rule R8: ₹ Indian grouping, dates as "08 Aug 2026"
 * (Asia/Kolkata). DECIMAL fields arrive from the API as strings — parse first.
 */

import type { Candidate } from '../../../services/recruitmentApi';

const KOLKATA = 'Asia/Kolkata';

/** Parse a DECIMAL-as-string (e.g. "2.0") to a number, or null if absent/invalid. */
export const parseDecimal = (value: string | number | null | undefined): number | null => {
	if (value === null || value === undefined || value === '') return null;
	const n = Number(value);
	return Number.isFinite(n) ? n : null;
};

/** Trim a trailing ".0" so "2.0" reads as "2" but "2.5" stays "2.5". */
const trimZeros = (n: number): string =>
	Number.isInteger(n) ? String(n) : String(n);

/** "0.0"–"2.0" → "0–2 yrs"; single bound → "2+ yrs" / "up to 2 yrs"; none → "—". */
export const formatExperienceRange = (
	min: string | number | null | undefined,
	max: string | number | null | undefined
): string => {
	const lo = parseDecimal(min);
	const hi = parseDecimal(max);
	if (lo === null && hi === null) return '—';
	if (lo !== null && hi !== null) return `${trimZeros(lo)}–${trimZeros(hi)} yrs`;
	if (lo !== null) return `${trimZeros(lo)}+ yrs`;
	return `up to ${trimZeros(hi as number)} yrs`;
};

/** Total experience for a person: "2.0" → "2 yrs", null → "—". */
export const formatExperience = (value: string | number | null | undefined): string => {
	const n = parseDecimal(value);
	return n === null ? '—' : `${trimZeros(n)} ${n === 1 ? 'yr' : 'yrs'}`;
};

/** "2026-08-08" or ISO → "08 Aug 2026" in Asia/Kolkata. */
export const formatDate = (value: string | null | undefined): string => {
	if (!value) return '—';
	const d = new Date(value.length === 10 ? `${value}T00:00:00+05:30` : value);
	if (Number.isNaN(d.getTime())) return '—';
	return new Intl.DateTimeFormat('en-IN', {
		day: '2-digit',
		month: 'short',
		year: 'numeric',
		timeZone: KOLKATA,
	}).format(d);
};

/** "08 Aug 2026, 3:45 PM" in Asia/Kolkata — for audit timestamps. */
export const formatDateTime = (value: string | null | undefined): string => {
	if (!value) return '—';
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return '—';
	return new Intl.DateTimeFormat('en-IN', {
		day: '2-digit',
		month: 'short',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		hour12: true,
		timeZone: KOLKATA,
	}).format(d);
};

/** "2 days ago", "just now", "3 hours ago" — for "Last activity". */
export const timeAgo = (value: string | null | undefined): string => {
	if (!value) return '—';
	const then = new Date(value).getTime();
	if (Number.isNaN(then)) return '—';
	const secs = Math.round((Date.now() - then) / 1000);
	if (secs < 45) return 'just now';
	const mins = Math.round(secs / 60);
	if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
	const hours = Math.round(mins / 60);
	if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
	const days = Math.round(hours / 24);
	if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
	const months = Math.round(days / 30);
	if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
	const years = Math.round(months / 12);
	return `${years} year${years === 1 ? '' : 's'} ago`;
};

/** Initials for an avatar chip: "Kavita Deshmukh" → "KD". */
export const initials = (name: string | null | undefined): string => {
	if (!name) return '?';
	const parts = name.trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) return '?';
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/** Recruiter display name from row fields, or "—". */
export const recruiterName = (
	first: string | null | undefined,
	last: string | null | undefined
): string => {
	const name = [first, last].filter(Boolean).join(' ').trim();
	return name || '—';
};

/** Today as YYYY-MM-DD in Asia/Kolkata — the default for `date_opened`. */
export const todayISO = (): string => {
	const parts = new Intl.DateTimeFormat('en-CA', {
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		timeZone: KOLKATA,
	}).format(new Date());
	return parts; // en-CA already yields YYYY-MM-DD
};

// ─── CTC restriction (rule R6) ───────────────────────────────────────────────

/**
 * CTC is field-level restricted: the key is ABSENT (not null) when the caller
 * is unauthorized. Distinguish "restricted" (key missing) from "not provided"
 * (key present but null) so the UI can show a 🔒 chip vs an em dash.
 */
export const ctcIsRestricted = (
	candidate: Candidate | null | undefined,
	field: 'current_ctc' | 'expected_ctc'
): boolean => !!candidate && !(field in candidate);
